import { useMemo } from 'react';
import { useI18nContext } from '@i18n/i18n-react';
import { TranslationFunctions } from '@i18n/i18n-types';

import { Option } from '@components/form';
import { LinkAlt, User, Users } from '@components/icons/FarseerIcons';
import { useUserGroupOptions, parseUserGroupKey } from './useUserGroupOptions';
import { useUserOptions, parseUserKey } from './useUserOptions';

export { getUserKey } from './useUserOptions';
export { getUserGroupKey } from './useUserGroupOptions';

export const PUBLIC_LINK_KEY = 'public';
export const getPublicLinkOption = (LL: TranslationFunctions) => ({
    value: PUBLIC_LINK_KEY,
    label: LL.PUBLIC_LINK(),
    icon: LinkAlt,
});

export const useUserAndGroupOptions = (config?: { publicLink?: boolean }) => {
    const { LL } = useI18nContext();
    const { usersMap, options: userOptions } = useUserOptions();
    const { userGroupsMap, options: userGroupOptions } = useUserGroupOptions();

    const allOptions: Option<string>[] = useMemo(() => {
        const options = [
            ...userOptions.map((o) => ({ value: o.key, label: o.label, icon: User, image: o.image })),
            ...userGroupOptions.map((o) => ({ value: o.key, label: o.label, icon: Users })),
        ];
        if (config?.publicLink) options.unshift({ value: PUBLIC_LINK_KEY, label: LL.PUBLIC_LINK(), icon: LinkAlt });
        return options;
    }, [LL, userOptions, userGroupOptions, config?.publicLink]);

    return { usersMap, userGroupsMap, allOptions };
};

export const parseUserAndGroupKeys = (keys: string[]) => ({
    userIds: keys.map(parseUserKey).filter((id) => id != null),
    userGroupIds: keys.map(parseUserGroupKey).filter((id) => id != null),
    publicLink: keys.includes(PUBLIC_LINK_KEY),
});
